import type { Engine } from "../../engine/Engine";
import {
  RENDER_SETTINGS_CHANGED_EVENT,
  renderSettingsStore,
  type RenderSettingsProductScope,
} from "./RenderSettingsStore";
import { applyRenderSettingsToHost } from "./applyRenderSettings";

export const DEFAULT_RENDER_SETTINGS_WAIT_FOR = [
  "ssgi-postprocess",
  "gtao-accumulate-postprocess",
  "sugar-taa",
  "sugar-path-tracer",
] as const;

export type RenderSettingsHostBinding = {
  apply: () => void;
  setEngine: (engine: Engine | undefined) => void;
  getEngine: () => Engine | undefined;
  connect: () => void;
  disconnect: () => void;
};

export type CreateRenderSettingsHostBindingOptions = {
  resolveApplyHost: () => ParentNode;
  resolveEngine?: () => Engine | undefined;
  eventTarget?: EventTarget;
  listenEngineReady?: boolean;
  waitForElements?: readonly string[];
  /** Isolates this host's defaults + persisted bag. */
  productScope?: RenderSettingsProductScope;
  /** false: sahne env’sine dokunma (host kendi DEFAULT HDR’ını yükler). */
  applyEnvironment?: boolean;
};

/**
 * Host pushes settings on engine-ready, settings-changed, and entity-created
 * (postprocess Lit entities). No polling.
 */
export function createRenderSettingsHostBinding(
  options: CreateRenderSettingsHostBindingOptions,
): RenderSettingsHostBinding {
  let engine: Engine | undefined;
  const waitFor = options.waitForElements ?? DEFAULT_RENDER_SETTINGS_WAIT_FOR;
  const listenEngineReady =
    options.listenEngineReady ?? options.eventTarget != null;

  const resolveEngine = (): Engine | undefined =>
    engine ?? options.resolveEngine?.();

  const apply = (): void => {
    const next = resolveEngine();
    if (!next?.activeScene) return;
    engine = next;
    applyRenderSettingsToHost(options.resolveApplyHost(), next, {
      applyEnvironment: options.applyEnvironment,
    });
  };

  const onSettingsChanged = (): void => {
    apply();
  };

  const onEngineReady = (event: Event): void => {
    const detail = (event as CustomEvent).detail as Engine | undefined;
    engine = detail ?? options.resolveEngine?.() ?? engine;
    apply();
  };

  const onEntityCreated = (): void => {
    apply();
  };

  const connect = (): void => {
    if (options.productScope) {
      renderSettingsStore.setProductScope(options.productScope);
    }
    renderSettingsStore.addEventListener(
      RENDER_SETTINGS_CHANGED_EVENT,
      onSettingsChanged,
    );
    if (listenEngineReady && options.eventTarget) {
      options.eventTarget.addEventListener("engine-ready", onEngineReady);
      // composed+bubbles from ssgi/gtao/taa/path-tracer firstUpdated
      options.eventTarget.addEventListener("entity-created", onEntityCreated);
    }
    void Promise.all(
      waitFor.map((name) => customElements.whenDefined(name)),
    ).then(() => requestAnimationFrame(apply));
  };

  const disconnect = (): void => {
    renderSettingsStore.removeEventListener(
      RENDER_SETTINGS_CHANGED_EVENT,
      onSettingsChanged,
    );
    if (listenEngineReady && options.eventTarget) {
      options.eventTarget.removeEventListener("engine-ready", onEngineReady);
      options.eventTarget.removeEventListener(
        "entity-created",
        onEntityCreated,
      );
    }
  };

  return {
    apply,
    setEngine: (next) => {
      engine = next;
    },
    getEngine: () => engine,
    connect,
    disconnect,
  };
}
